/**
 * MiniProjectDataReliabilityTab — Task #231.
 *
 * Body for the Data Reliability sub-tab inside MiniProjectKpiNavigation.
 * Relocation of DataReliabilityModal's body — same data sources, same
 * threshold math, same visual language, modal chrome (backdrop, scrim,
 * portal, close ×) stripped.
 *
 * Per Skip's 2026-05-19 decision, Data Reliability is "staleness only"
 * in v1: green ≤ 3 days, yellow 4–7 days, red > 7 days, grey if no
 * timestamp. This tab is a read-only explainer of why the current color
 * is what it is — the value is auto-derived from the most recent
 * sub-entity write.
 *
 * Composition (top → bottom):
 *
 *   1. Headline card — eyebrow + band chip top-right + big number
 *      ("X days") + threshold-band bar with marker.
 *   2. Last-update detail card — timestamp + source field.
 *   3. Lead hint card (Lead only) OR public hint card (public users
 *      when days > YELLOW_MAX_DAYS).
 *
 * The threshold constants (GREEN_MAX_DAYS = 3, YELLOW_MAX_DAYS = 7,
 * CHART_MAX_DAYS = 14) are kept inline here AND in computeKpiStatus.ts.
 * If either changes, change both. Canonical source remains
 * computeKpiStatus.ts.
 */

import { useAppTranslation } from "../../i18n/useAppTranslation";
import type { MiniProject } from "../../api/miniProjects";
import styles from "./MiniProjectDataReliabilityTab.module.css";

/* ── Types ─────────────────────────────────────────────────────── */

interface Props {
  miniProject: MiniProject;
  isLead: boolean;
}

/* Thresholds match computeKpiStatus.ts. Kept inline so this component
 * is self-documenting — but the canonical source is computeKpiStatus.
 * If you change one, change both. */
const GREEN_MAX_DAYS = 3;
const YELLOW_MAX_DAYS = 7;
/* Visual cap for the threshold bar — anything past this draws at the
 * rightmost position. Doesn't change the color rule, just the chart
 * geometry. */
const CHART_MAX_DAYS = 14;

/* ── Helpers ───────────────────────────────────────────────────── */

/**
 * Pick the freshest timestamp source available on the mini-project.
 * Returns the ISO string and an i18n key for the source label so the
 * caller can resolve via t() at render time (sources are localized).
 */
function pickTimestamp(mp: MiniProject): { iso: string; sourceKey: string } | null {
  if (mp.lastDataUpdateAt) {
    return {
      iso: mp.lastDataUpdateAt,
      sourceKey: "miniProject.kpiModals.dataRel.source.subEntity",
    };
  }
  if (mp.updatedAt) {
    return {
      iso: mp.updatedAt,
      sourceKey: "miniProject.kpiModals.dataRel.source.project",
    };
  }
  return null;
}

function daysBetween(iso: string): number {
  const ms = Date.now() - new Date(iso).getTime();
  return Math.max(0, ms / 86400000);
}

function classify(days: number | null): {
  color: "green" | "yellow" | "red" | "grey";
  labelKey: string;
} {
  if (days === null) {
    return {
      color: "grey",
      labelKey: "miniProject.kpiModals.dataRel.band.noTimestamp",
    };
  }
  if (days <= GREEN_MAX_DAYS) {
    return {
      color: "green",
      labelKey: "miniProject.kpiModals.dataRel.band.fresh",
    };
  }
  if (days <= YELLOW_MAX_DAYS) {
    return {
      color: "yellow",
      labelKey: "miniProject.kpiModals.dataRel.band.gettingStale",
    };
  }
  return {
    color: "red",
    labelKey: "miniProject.kpiModals.dataRel.band.stale",
  };
}

function formatLongDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/* ── Component ─────────────────────────────────────────────────── */

export default function MiniProjectDataReliabilityTab({
  miniProject,
  isLead,
}: Props) {
  const { t } = useAppTranslation("pages");

  const ts = pickTimestamp(miniProject);
  const days = ts ? daysBetween(ts.iso) : null;
  const band = classify(days);

  // Marker position on the bar: 0..1 fraction of CHART_MAX_DAYS.
  const markerPct =
    days === null ? 0 : Math.min(1, days / CHART_MAX_DAYS);
  const greenEnd = (GREEN_MAX_DAYS / CHART_MAX_DAYS) * 100;
  const yellowEnd = (YELLOW_MAX_DAYS / CHART_MAX_DAYS) * 100;

  return (
    <div className={styles.root}>
      {/* ── Headline card ────────────────────────────────────── */}
      <div className={styles.headlineCard}>
        <div className={styles.headlineHeader}>
          <span className={styles.eyebrow}>
            {t("miniProject.kpiModals.dataRel.title")}
          </span>
          <span className={`${styles.bandChip} ${styles[`band_${band.color}`]}`}>
            <span className={styles.bandDot} aria-hidden="true" />
            {t(band.labelKey)}
          </span>
        </div>

        <div className={styles.bigNumber}>
          <div className={`${styles.bigNumberValue} ${styles[`band_${band.color}_text`]}`}>
            {days === null ? "—" : days < 1 ? "< 1" : Math.floor(days)}
          </div>
          <div className={styles.bigNumberLabel}>
            {days === null
              ? t("miniProject.kpiModals.dataRel.bigLabel.noTimestamp")
              : Math.floor(days) === 1
                ? t("miniProject.kpiModals.dataRel.bigLabel.day")
                : t("miniProject.kpiModals.dataRel.bigLabel.days")}
          </div>
        </div>

        <div className={styles.barWrap}>
          <div className={styles.barTitle}>
            {t("miniProject.kpiModals.dataRel.thresholdBand")}
          </div>
          <div className={styles.bar}>
            <div
              className={`${styles.barZone} ${styles.barZoneGreen}`}
              style={{ inlineSize: `${greenEnd}%` }}
            >
              <span className={styles.barZoneLabel}>
                {t("miniProject.kpiModals.dataRel.zone.green", { max: GREEN_MAX_DAYS })}
              </span>
            </div>
            <div
              className={`${styles.barZone} ${styles.barZoneYellow}`}
              style={{ inlineSize: `${yellowEnd - greenEnd}%` }}
            >
              <span className={styles.barZoneLabel}>
                {t("miniProject.kpiModals.dataRel.zone.yellow", {
                  lo: GREEN_MAX_DAYS + 1,
                  hi: YELLOW_MAX_DAYS,
                })}
              </span>
            </div>
            <div
              className={`${styles.barZone} ${styles.barZoneRed}`}
              style={{ inlineSize: `${100 - yellowEnd}%` }}
            >
              <span className={styles.barZoneLabel}>
                {t("miniProject.kpiModals.dataRel.zone.red", { min: YELLOW_MAX_DAYS + 1 })}
              </span>
            </div>
            {days !== null && (
              <div
                className={styles.barMarker}
                style={{ insetInlineStart: `${markerPct * 100}%` }}
                aria-label={t("miniProject.kpiModals.dataRel.markerAria", {
                  days: Math.floor(days),
                })}
              />
            )}
          </div>
        </div>
      </div>

      {/* ── Last-update detail card ──────────────────────────── */}
      {ts && (
        <div className={styles.detailCard}>
          <div className={styles.detailLabel}>
            {t("miniProject.kpiModals.dataRel.lastUpdate")}
          </div>
          <div className={styles.detailValue}>{formatLongDateTime(ts.iso)}</div>
          <div className={styles.detailSource}>
            {t("miniProject.kpiModals.dataRel.source.label", {
              source: t(ts.sourceKey),
            })}
          </div>
        </div>
      )}

      {/* ── Hint card ─────────────────────────────────────────
          Lead always sees how to refresh; non-lead users only see a
          hint when the project is past the yellow threshold (so a
          fresh project doesn't carry a confusing public-warning). */}
      {isLead ? (
        <div className={styles.leadHint}>
          {t("miniProject.kpiModals.dataRel.leadHint")}
        </div>
      ) : (
        days !== null &&
        days > YELLOW_MAX_DAYS && (
          <div className={styles.publicHint}>
            {t("miniProject.kpiModals.dataRel.publicHint")}
          </div>
        )
      )}
    </div>
  );
}
